import { Box, Paper, Typography, styled } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import React from 'react'
import { type Alias } from '../../api/types'
import { Address } from '../address'
import {
  Alias as AliasChip,
  ConflictAlias,
  PendingAlias
} from './desktop.alias'
import { RemoveGroupButton } from './group.actionButtons'
import { type GroupItemProps } from './mobile'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: 0,
  textAlign: 'left',
  color: theme.palette.text.secondary,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderTop: `3px solid #c4e3f0`,
  boxShadow: 'none',
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: theme.shadows[2]
  }
}))

const today = new Date().toISOString().split('T')[0]

export const Group: React.FC<GroupItemProps> = ({
  name,
  email,
  search,
  members,
  pendingAlias,
  deletingAlias,
  aliases,
  allAliases,
  onDeleteGroup,
  onDeleteAlias,
  onAliasUpdated,
  onToggleAlias,
  conflicts,
  onResolveConflict,
  isGroupDeleting
}) => {
  const isEmpty = allAliases.length === 0 && pendingAlias === undefined

  const expiredAliases = aliases.filter(
    (a) => a.expires !== '' && a.expires < today
  )
  const activeAliases = aliases.filter(
    (a) => a.expires === '' || a.expires >= today
  )

  const renderChip = (alias: Alias): React.JSX.Element => (
    <AliasChip
      key={alias.alias}
      alias={alias}
      search={search}
      deletingAlias={deletingAlias}
      onDeleteAlias={onDeleteAlias}
      onAliasUpdated={onAliasUpdated}
      onToggleAlias={onToggleAlias}
    />
  )

  return (
    <Item elevation={0}>
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.5,
          borderBottom: isEmpty ? 'none' : '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'grey.50',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color={isGroupDeleting ? 'text.disabled' : 'text.primary'}
            sx={{
              lineHeight: 1.3,
              textDecoration: isGroupDeleting ? 'line-through' : 'none'
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="body2"
            component="div"
            color={isGroupDeleting ? 'text.disabled' : 'primary.main'}
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              mt: 0.25,
              textDecoration: isGroupDeleting ? 'line-through' : 'none'
            }}
          >
            <Address value={email} search={search} />
          </Typography>
          {members?.length > 0 && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {members.map((m) => m.split('@')[0]).join(' · ')}
            </Typography>
          )}
        </Box>
        {isEmpty &&
          (isGroupDeleting ? (
            <CircularProgress size={24} />
          ) : (
            <RemoveGroupButton onClick={() => onDeleteGroup(email)} />
          ))}
      </Box>
      {!isEmpty && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center'
          }}
        >
          {conflicts.length > 0 && (
            <Box
              sx={(theme) => ({
                width: '100%',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(244, 67, 54, 0.08)'
                    : '#fff2f2',
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: 1,
                px: 1,
                pt: 0.75,
                pb: 0.75,
                mb: 1
              })}
            >
              <Typography
                variant="caption"
                color="error.main"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              >
                Conflict
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}
              >
                {conflicts.map((conflict) => (
                  <ConflictAlias
                    key={conflict.enabled}
                    conflict={conflict}
                    search={search}
                    deletingAlias={deletingAlias}
                    onResolveConflict={onResolveConflict}
                  />
                ))}
              </Box>
            </Box>
          )}
          {expiredAliases.length > 0 && (
            <Box
              sx={(theme) => ({
                width: '100%',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 167, 38, 0.08)'
                    : '#fff8f0',
                border: '1px solid',
                borderColor: 'warning.main',
                borderRadius: 1,
                px: 1,
                pt: 0.75,
                pb: 0.75,
                mb: 1
              })}
            >
              <Typography
                variant="caption"
                color="warning.main"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              >
                Expired
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  mt: 0.5
                }}
              >
                {expiredAliases.map(renderChip)}
              </Box>
            </Box>
          )}
          {activeAliases.map(renderChip)}
          {pendingAlias !== undefined && <PendingAlias alias={pendingAlias} />}
          {aliases.length === 0 && (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontStyle: 'italic' }}
            >
              No emails with &apos;{search}&apos;
            </Typography>
          )}
        </Box>
      )}
    </Item>
  )
}
